import {
  Component,
  Output,
  Input,
  OnInit,
  OnDestroy,
  EventEmitter,
  ViewEncapsulation,
  ChangeDetectorRef,
  SimpleChanges,
  OnChanges
} from '@angular/core';
import { select, Store } from '@ngrx/store';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import {
  Negotiation,
  WelcomeVoucher,
  Product,
  Shipping,
  User,
  AlertsService,
  AlertSettings,
  AlertCriterion
} from '@vestiaire/api-sdk-angular';
import { SizeGuideModalComponent } from '@app/shared/components/size-guide-modal/size-guide-modal.component';
import { ProductReservation } from '@vestiaire/api-sdk-angular/model/productReservation';
import { Subscription } from 'rxjs';
import { UserTransactionRole, UserService } from '@app/core/user';
import { LocaleService } from '@app/core/i18n';
import { SizeGuide } from '@app/shared/model/interfaces/sizeGuide/sizeGuide';
import { IAppState, MmaoCtaClick } from '@app/store';
import { ReservationService } from '@app/shared/services/product';
import { MmaoData } from '@app/analytics/models/types';
import * as fromUser from '@app/core/user/store/reducers';
import * as fromProduct from '@app/+product/store/reducers';
import { AddPicturesComponent } from '@app/+product/components/product-details/add-pictures';
import { MmaoService } from '@app/shared/components/mmao/services/mmao.service';
import { LocalisationModalComponent } from '@app/+product/components/product-details/localisation-modal/localisation-modal.component';
import { DeliveryModalComponent } from '@app/+product/components/product-details/delivery-modal/delivery-modal.component';
import { ControlQualityModalComponent } from './control-quality-modal/control-quality-modal.component';
import * as fromCart from '@app/core/cart/reducers';
import * as localForage from 'localforage';
import { fromPromise } from 'rxjs/internal/observable/fromPromise';
import * as dayjs from 'dayjs';
import { ClickConsigmentCTA } from '@app/+product/store';
import { ModalDeliveryServiceDescriptionComponent } from '@app/shared/components/modals/modal-delivery-service-description/modal-delivery-service-description.component';
import { AlgoliaInsightService } from '@app/shared/components/catalog/services/algolia-insight.service';
import { AlertCriterionCollection } from '@app/shared/model/classes/alert-criterion-collection';
import {
  CreateHolidaysEndAlertSuccess,
  DeleteHolidaysEndAlertSuccess
} from '@app/+product/store/actions/product.action';
import { TrackingService } from '@app/analytics/services/tracking.service';
import { TagContainer } from '@app/analytics/models/container';
import { environment } from '@env/environment';
import { ChatService } from '@app/+chat/services/chat.service';
import { getUser } from '@app/core/user/store/reducers';

type modalDeliveryType = 'directShippingBuyer' | 'expressDelivery' | 'directShippingTopTier';

@Component({
  selector: 'vc-product-details',
  templateUrl: './product-details.component.html',
  styleUrls: ['./product-details.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ProductDetailsComponent implements OnInit, OnChanges, OnDestroy {
  @Input()
  product: Product;
  @Input()
  shipping: Shipping | null;

  @Input()
  user: User | null;
  @Input()
  userRole: UserTransactionRole = 'buyer';

  @Input()
  idSite: string;
  @Input()
  regionCode: string;
  @Input()
  sizeGuide: SizeGuide | null;
  @Input()
  negotiation: Negotiation | null;

  @Input()
  shippingVoucherPath: string;

  @Input()
  public directShippingEligible: boolean;

  @Output()
  openConditionGuideModal = new EventEmitter();

  public welcomeVoucher: WelcomeVoucher;
  public isWelcomeVoucherInfosActive = false;

  public reservation: ProductReservation | null;
  public reservationExpirationDate: Date | null;
  public isDirectShippingTaxes: boolean;

  protected directShippingPopinContext = 'directShippingBuyer';

  private subscriptions: Array<Subscription> = [];

  isOpen = false;

  public isInCourseOfPurchase = false;
  public shippingFees = '';
  public displayTaxeInfo = false;
  public isInCart = false;
  public disableBtnCancelMySale = false;
  public defaultModalConfig: MatDialogConfig = {
    disableClose: false,
    maxWidth: 600,
    data: {}
  };
  public isSellerOnVacation = false;
  public isHolidaysEndAlertCreated = false;
  public holidaysEndAlertError: string;
  public sustainableLeafOn = environment.feature.sustainable_leaf || false;
  private holidaysEndAlertId: string;
  public chat_from_pdp = environment.feature.chat_from_pdp || false;
  public isEligibleForChat: boolean;
  public chatAllowed = true;

  private isLocal: boolean;

  constructor(
    private store: Store<IAppState>,
    private userService: UserService,
    private dialogService: MatDialog,
    private changeDetector: ChangeDetectorRef,
    private reservationService: ReservationService,
    private localeService: LocaleService,
    private mmaoService: MmaoService,
    private algoliaInsightService: AlgoliaInsightService,
    private alertsService: AlertsService,
    private chatService: ChatService,
    private trackingService: TrackingService
  ) {
    this.displayTaxeInfo = this.localeService.shouldDisplayTaxes();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.shipping && this.displayTaxeInfo) {
      this.shippingFees = this.getShippingFees();
    }
  }

  ngOnInit() {
    this.subscriptions.push(
      this.store.pipe(select(fromUser.getWelcomeVoucher)).subscribe(
        res => this.setWelcomeVoucher(res),
        err => this.setWelcomeVoucher()
      )
    );

    this.subscriptions.push(
      this.store.pipe(select(fromProduct.selectReservationState)).subscribe(
        state => {
          const reservation = state && state.productId === this.product.id ? state.status : null;
          this.setReservation(reservation);
        },
        error => this.setReservation()
      )
    );

    this.initCartSub();
    this.initBtnCancelMySale();
    if (this.product && this.product.seller.vacation) {
      this.isSellerOnVacation = this.product.seller.vacation.active;
    }

    if (
      this.product &&
      this.product.seller &&
      this.product.seller.dsSellerType &&
      this.product.seller.dsSellerType === 'TOPTIER'
    ) {
      this.directShippingPopinContext = 'directShippingTopTier';
    }

    this.subscriptions.push(
      this.store.pipe(select(getUser)).subscribe({
        next: res => {
          if (res) {
            this.chatEligibility(res.id);
          } else {
            this.chatAllowed = true;
            this.isEligibleForChat = false;
          }
        },
        error: console.error
      })
    );

    this.isLocal = this.product?.isLocal || false;
  }

  chatEligibility(userId: string | '') {
    // check chat eligibity, it returns eligibity for using the chat
    if (this.userRole !== 'seller') {
      const payload = {
        buyerId: userId,
        sellerId: this.product?.seller?.id,
        sellerSegment: this.product?.seller?.segment,
        productId: this.product?.id
      };
      const { buyerId, sellerId, sellerSegment, productId } = payload;
      if (buyerId && sellerId && sellerSegment && productId) {
        this.chatService
          .checkChatEligibility(payload)
          .then(res => {
            if (!res) {
              this.chatAllowed = false;
            }
            this.isEligibleForChat = res;
          })
          .catch(err => {
            console.log('Chat eligibility api error', err);
          });
      }
    } else {
      this.isEligibleForChat = false;
      this.chatAllowed = false;
    }
    this.isDirectShippingTaxes = this.product?.dutyAndTax?.cents > 0 || false;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => {
      subscription.unsubscribe();
    });
  }

  get showBuyerCallToActions(): boolean {
    return this.isShippable && !this.product.sold && !(this.isReserved && this.reservation.reservedToMe);
  }

  get canAddToCart(): boolean {
    return (
      this.isShippable && !this.product.sold && !(this.isReserved && this.reservation.reservedToMe) && !this.isInCart
    );
  }

  /**
   * Checks if currently signed-in user is allowed to make an offer.
   */
  get canMakeAnOffer(): boolean {
    if (this.product) {
      return this.mmaoService.canMakeAnOffer(this.product, this.reservation, this.negotiation);
    }

    return false;
  }

  setReservation(reservation?: ProductReservation): void {
    let expiresAt = null;

    if (reservation && reservation.expirationDate) {
      expiresAt = new Date(reservation.expirationDate);
    }

    this.reservation = reservation;
    this.reservationExpirationDate = expiresAt;

    this.changeDetector.markForCheck();
  }

  get isReserved(): boolean {
    return this.reservation && this.reservationService.isValid(this.reservation);
  }

  get isShippable(): boolean {
    return this.shipping && this.shipping.buyable && !this.shipping.prohibited;
  }

  get isLogged(): boolean {
    return this.user !== null;
  }

  get isSeller(): boolean {
    return this.userRole === 'seller';
  }

  get isPremium(): boolean {
    return this.userService.isPremium(this.user);
  }

  get isOfficialStore(): boolean {
    return this.userService.isOfficialStore(this.product.seller.badges);
  }

  toggleEditorsPick(): void {
    this.isOpen = !this.isOpen;
  }

  openDeliveryModal(): void {
    const config: any = {
      disableClose: false,
      maxWidth: 600,
      data: {
        product: this.product,
        sendDay: this.shipping.sendDay,
        warehouse: this.shipping.warehouse.name,
        sendDelay: this.shipping.sendDelay,
        pays: this.shipping.destinationCountry.name
      }
    };
    this.dialogService.open(DeliveryModalComponent, config);
  }

  openLocalisationModal(): void {
    const config: any = {
      disableClose: false,
      maxWidth: 600,
      data: {
        product: this.product,
        isLocal: this.isLocal
      }
    };
    this.dialogService.open(LocalisationModalComponent, config);
  }

  openControlQualityModal(): void {
    const config: any = {
      disableClose: false,
      maxWidth: 600
    };
    this.dialogService.open(ControlQualityModalComponent, config);
  }

  openSizeGuideModal() {
    this.dialogService.open(SizeGuideModalComponent, {
      maxWidth: 800,
      data: {
        size: this.product.size['size'],
        standard: this.product.size['standard'],
        sizeGuide: this.sizeGuide
      }
    });
  }

  conditionGuideModalComponent(): void {
    this.openConditionGuideModal.emit();
  }

  setWelcomeVoucher(voucher?: WelcomeVoucher): void {
    if (voucher && voucher.voucherCode === '') {
      voucher = null;
    }
    this.welcomeVoucher = voucher;
    this.changeDetector.markForCheck();
  }

  getShippingFees() {
    if (this.shipping) {
      if (this.product.price.cents > this.shipping.cost.highValue.cents) {
        return this.shipping.cost.highAmount.formatted;
      } else {
        return this.shipping.cost.lowAmount.formatted;
      }
    }
    return '';
  }

  trackChatClick() {
    const payload = {
      event: 'bs_chat',
      chat_action_name: 'open_from_product',
      chat_context: 'product_buyer'
    };

    this.trackingService.send({
      type: 'event',
      container: TagContainer.Analytics,
      payload
    });
  }

  trackMmaoClick() {
    const data = {
      product: this.product,
      userRole: this.userRole
    } as MmaoData;

    this.store.dispatch(new MmaoCtaClick(data));
  }

  trackConsignmentClick() {
    this.store.dispatch(new ClickConsigmentCTA('product_details'));
  }

  trackAddToCart(): void {
    this.algoliaInsightService.addToCart(this.product.id.toString());
  }

  toogleWelcomeVoucherinfo() {
    this.isWelcomeVoucherInfosActive = !this.isWelcomeVoucherInfosActive;
  }
  closeWelcomeVoucherinfo() {
    this.isWelcomeVoucherInfosActive = false;
  }

  openAddPhotoModal() {
    const config: MatDialogConfig = {
      data: {
        productId: this.product.id
      }
    };
    this.dialogService.open(AddPicturesComponent, { ...this.defaultModalConfig, ...config });
  }

  initCartSub() {
    this.store.select(fromCart.getCurrentCart).subscribe(
      res => {
        this.isInCart = false;
        if (res && res.orderLines && Array.isArray(res.orderLines)) {
          this.isInCart = res.orderLines.filter(ol => ol.product.id === this.product.id).length > 0;
        }
        this.changeDetector.markForCheck();
      },
      () => {}
    );
  }

  initBtnCancelMySale(): void {
    fromPromise(localForage.getItem('dateBtnCancelMyDate')).subscribe(
      res => {
        if (res) {
          const beginDate = dayjs(res + '');
          const dNow = dayjs(new Date());
          const diffMinutes = dNow.diff(beginDate, 'minute');
          const limitDurationForCache = 15;
          if (diffMinutes > limitDurationForCache) {
            localForage.removeItem('dateBtnCancelMyDate');
            this.disableBtnCancelMySale = false;
          } else {
            this.disableBtnCancelMySale = true;
          }
          this.changeDetector.markForCheck();
        }
      },
      () => {}
    );
  }
  setStartDateCancelMySale(event): void {
    if (!this.disableBtnCancelMySale) {
      this.disableBtnCancelMySale = true;
      localForage.setItem('dateBtnCancelMyDate', new Date());
    }
  }

  canShowMessageBloc(): boolean {
    return (
      !this.isSeller &&
      this.negotiation &&
      (this.negotiation.isAccepted ||
        !this.negotiation.nextActions ||
        (this.negotiation.lastEvent.action === 'offer' && this.negotiation.lastEvent.createdBy === 'seller'))
    );
  }

  openDeliveryServiceDescriptionPopin(type: modalDeliveryType) {
    this.dialogService.open(ModalDeliveryServiceDescriptionComponent, {
      maxWidth: 600,
      data: {
        type: type,
        region: this.regionCode
      }
    });
  }
  toggleHolidaysEndAlert() {
    if (!this.user) {
      return;
    }

    this.isHolidaysEndAlertCreated && this.holidaysEndAlertId !== ''
      ? this.deleteHolidaysEndAlert()
      : this.createHolidaysEndAlert();
  }

  createHolidaysEndAlert() {
    const criterions: AlertCriterionCollection = new AlertCriterionCollection();
    const productCriterion: AlertCriterion = { key: 'idProduct', value: [this.product.id as string] };
    criterions.addCriterion(productCriterion);
    const data: AlertSettings = {
      category: 'sellerBackHoliday',
      criteria: criterions.getList()
    };

    this.alertsService.createAlert(data).subscribe(
      res => {
        if (res.data && res.data.id) {
          this.holidaysEndAlertId = res.data.id;
          this.isHolidaysEndAlertCreated = true;
        }
        this.store.dispatch(new CreateHolidaysEndAlertSuccess(this.product));
      },
      error => {
        console.error(error);
        this.holidaysEndAlertError = error.error.errors.detail;
      }
    );
  }

  deleteHolidaysEndAlert() {
    this.alertsService.deleteAlert(this.holidaysEndAlertId).subscribe(
      res => {
        this.isHolidaysEndAlertCreated = false;
        this.holidaysEndAlertId = '';
        this.store.dispatch(new DeleteHolidaysEndAlertSuccess(this.product));
      },
      error => {
        console.error(error);
      }
    );
  }
}
